import CustomText from "@components/CustomText";
import { colors } from "@constants/theme";
import React, { useEffect, useState } from "react";
import Cross from "@assets/svg/CrossIcon";
import InfoCircle from "@assets/svg/InfoCircle";
import {
  Keyboard,
  StyleSheet,
  TouchableOpacity,
  View,
  Modal,
  TextInput,
  ScrollView,
  TouchableWithoutFeedback,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import BottomButton from "./BottomButton";

const AddBagNoModalContent = ({
  onClose,
  onSave,
  item,
  error = "",
  btnLoading,
}) => {
  // Insets come from the SafeAreaProvider nested inside the Modal (see the
  // wrapper below), so this reflects the modal's own translucent window. The
  // root provider reports bottom: 0 while a navigationBarTranslucent modal is
  // open, which would leave the Save bar clipped behind the soft-nav bar.
  const insets = useSafeAreaInsets();
  const [bagNumbers, setBagNumbers] = useState(item?.bags || [""]);
  const [focusedIndex, setFocusedIndex] = useState(null);
  // Edge-to-edge modal windows on Android 15 no longer resize for the soft
  // keyboard. Lift the compact sheet by the keyboard height (reposition, not
  // internal padding — padding double-counts with the Save-bar clearance and
  // leaves the dead white band between the last field and the keyboard).
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const showSub = Keyboard.addListener("keyboardDidShow", (e) =>
      setKeyboardHeight(e.endCoordinates.height),
    );
    const hideSub = Keyboard.addListener("keyboardDidHide", () =>
      setKeyboardHeight(0),
    );
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const PATH =
    "screens/MyDeliveries/SortingDeliveries/components/AddBagNoModal";

  const handleAddBag = () => {
    setBagNumbers([...bagNumbers, ""]);
  };

  const handleChangeBag = (index, value) => {
    const newBagNumbers = [...bagNumbers];
    newBagNumbers[index] = value;
    setBagNumbers(newBagNumbers);
  };

  const closeModel = () => {
    setBagNumbers(item?.bags || [""]);
    setFocusedIndex(null);
    onClose();
  };

  return (
    <View style={styles.flex}>
      <TouchableWithoutFeedback onPress={closeModel}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={[styles.contentContainer, { bottom: keyboardHeight }]}>
        <View style={styles.modalCross}>
          <TouchableWithoutFeedback onPress={closeModel}>
            <Cross width={16} height={16} />
          </TouchableWithoutFeedback>
        </View>
        <View style={styles.headingContainer}>
          <View>
            <CustomText variant="title_20">{t(`${PATH}/addBagNo`)}</CustomText>
            <CustomText
              variant="body_text_2"
              style={{ color: colors.mediumBlacklightBlack }}
            >
              {`${item?.route_name} - ${item?.db_name || "Unassigned"} `}
            </CustomText>
          </View>
        </View>
        <ScrollView
          style={[
            styles.sectionContainer,
            { marginBottom: 60 + insets.bottom },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          {bagNumbers.map((bagNumber, index) => (
            <TextInput
              key={index}
              testID="sd-bag-no"
              style={[
                styles.textInput,
                focusedIndex === index && styles.textInputFocused,
              ]}
              value={bagNumber.toString() || ""}
              onChangeText={(text) => handleChangeBag(index, text)}
              placeholderTextColor="rgba(0, 0, 0, 0.20)"
              placeholder={t(`${PATH}/placeholder`)}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              autoFocus={index === bagNumbers.length - 1}
              keyboardType="number-pad"
            />
          ))}
          {error.length > 0 && (
            <View style={styles.errorContainer}>
              <InfoCircle />
              <CustomText variant="caption_2" style={styles.errorText}>
                {error}
              </CustomText>
            </View>
          )}
          <TouchableOpacity
            testID="sd-add-bag"
            onPress={handleAddBag}
            style={styles.addButton}
          >
            <CustomText variant="subtitle_16" style={styles.addButtonText}>
              {t(`${PATH}/newBag`)}
            </CustomText>
          </TouchableOpacity>
        </ScrollView>
        <BottomButton
          onPress={() => {
            onSave(bagNumbers.every((bag) => bag === "") ? [] : bagNumbers);
            setFocusedIndex(null);
          }}
          title={
            bagNumbers.every((bag) => bag === "")
              ? t(`${PATH}/saveWithoutBag`)
              : t(`${PATH}/save`)
          }
          loading={btnLoading}
          bottomInset={insets.bottom}
        />
      </View>
    </View>
  );
};

const AddBagNoModal = ({ visible, onClose, ...rest }) => {
  return (
    <Modal
      animationType="fade"
      transparent={true}
      statusBarTranslucent
      navigationBarTranslucent
      visible={visible}
      onRequestClose={onClose}
    >
      {/* Nested provider measures the modal's own translucent window so
          useSafeAreaInsets() reports the real nav-bar inset inside the Modal. */}
      <SafeAreaProvider>
        <AddBagNoModalContent onClose={onClose} {...rest} />
      </SafeAreaProvider>
    </Modal>
  );
};

export default AddBagNoModal;

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  sectionContainer: {
    padding: 12,
    paddingTop: 0,
    paddingLeft: 16,
    marginBottom: 60,
    maxHeight: "70%",
  },

  modalBackground: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  contentContainer: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
  },

  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    paddingTop: 20,
  },

  textInput: {
    borderWidth: 1,
    borderColor: colors.black,
    padding: 8,
    paddingLeft: 16,
    minHeight: 80,
    fontSize: 32,
    fontWeight: "bold",
    margin: 8,
    borderRadius: 2,
  },

  textInputFocused: {
    borderColor: colors.primary,
  },

  addButton: {
    width: "30%",
    margin: 8,
    borderColor: colors.plain_red,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: "center",
    marginBottom: 12,
  },

  addButtonText: {
    color: colors.plain_red,
    padding: 8,
  },
  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: colors.white,
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderColor: colors.lighterBlack,
    borderWidth: 1,
    borderRadius: 4,
    padding: 8,
    marginHorizontal: 8,
  },
  errorText: {
    color: colors.mediumBlack,
    marginLeft: 8,
  },
});
